import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { PAGE_SIZE } from "@shared/utils/constants";

// Types for the component
export interface MonthlyAverageData {
  parameter: string;
  value: string;
  unit: string;
  guideline: string;
  status: "Compliant" | "Non-Compliant";
}

export interface ViolationData {
  parameter: string;
  value: string;
  unit: string;
  guideline: string;
  exceedance: string;
  date: string;
}

interface ComplianceReportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  reportData: any;
  title?: string;
}

export const ComplianceReportDialog = ({
  isOpen,
  onClose,
  reportData,
  title = "Compliance Report",
}: ComplianceReportDialogProps) => {
  const createPagination = (totalItems: number) => ({
    currentPage: 1,
    totalPages: Math.ceil(totalItems / PAGE_SIZE),
    totalItems,
    itemsPerPage: PAGE_SIZE,
    startItem: 1,
    endItem: Math.min(PAGE_SIZE, totalItems),
  });

  const handleDownload = () => {
    // Implement download logic here
    console.log("Download clicked");
  };

  const monthlyAverageColumns: TableColumn<MonthlyAverageData>[] = [
    {
      key: "parameterName",
      header: "Parameter",
      type: "text",
    },
    {
      key: "averageValue",
      header: "Average Value",
      type: "text",
      render: (value) => (
        <span className="text-blue-600 font-semibold">{value}</span>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      type: "text",
    },
    {
      key: "guideline",
      header: "Guideline",
      type: "text",
    },
    {
      key: "status",
      header: "Status",
      type: "custom",
      render: (value) => (
        <Badge
          className={
            value === "Compliant"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }
        >
          {value}
        </Badge>
      ),
    },
  ];

  const violationsColumns: TableColumn<ViolationData>[] = [
    {
      key: "parameterName",
      header: "Parameter",
      type: "text",
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      key: "measuredValue",
      header: "Measured Value",
      type: "text",
      render: (value) => (
        <span className="text-red-600 font-semibold">{value ?? "NA"}</span>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      type: "text",
    },
    {
      key: "guideline",
      header: "Guideline",
      type: "text",
    },
    {
      key: "exceedance",
      header: "Exceedance",
      type: "text",
      render: (value) => (
        <span className="text-red-600 font-semibold">{value}</span>
      ),
    },
    {
      key: "date",
      header: "Date",
      type: "text",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <div className="flex justify-between items-center">
            <div className="space-y-1">
              <p className="text-sm text-gray-600">
                Report Date: {reportData?.reportDate}
              </p>
              <p className="text-sm text-gray-600">
                Analysis Period: {reportData?.analysisPeriod}
              </p>
            </div>
            {/* <Button onClick={handleDownload} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Download Report
            </Button> */}
            {/* Removed for now will need after */}
          </div>
        </DialogHeader>
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <p className="page-title">Monthly Average Quality Parameters</p>
              <p className="page-subtitle">
                Averaged values for all monitored parameters this month
              </p>
            </CardHeader>
            <CardContent className="p-0 mt-3">
              <GenericTable
                data={reportData?.complianceData?.parameters || []}
                columns={monthlyAverageColumns}
                rowKey="parameter"
                emptyMessage="No monthly average parameters found"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <p className="page-title">Violations</p>
              <p className="page-subtitle">
                Parameters that exceeded guidelines during the monitoring period
              </p>
            </CardHeader>
            <CardContent className="p-0 mt-3">
              <GenericTable
                data={reportData?.complianceData?.parameters || []}
                columns={violationsColumns}
                rowKey="parameter"
                emptyMessage="No violations found"
              />
            </CardContent>
          </Card>
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
